<template>
    <div
        v-if="isVisible && !isValueHidden && visibleValue"
        class="LinkedInValuePreview flex items-baseline justify-between gap-2"
    >
        <div class="flex items-baseline gap-1 text-xs">
            <CBadge v-if="visibleProvider" class="text-[0.6rem] font-medium leading-[0.85rem]" thin-text>
                {{ visibleProvider }}
            </CBadge>

            <div class="flex text-xs">
                <slot v-if="$slots.value" name="value" :output="visibleValue" />

                <CButton
                    v-else-if="type === 'url'"
                    class="!min-h-0 !p-0 !text-xs text-current [&_.btn\_\_icon]:min-h-0 [&_.btn\_\_icon]:min-w-0"
                    :class="'!pl-0 !pr-0'"
                    :href="visibleValue as string"
                    icon-right
                    target="_blank"
                    variant="link"
                >
                    <template #icon>
                        <CIcon class="size-3" name="open-in-new-tab" />
                    </template>
                    Navigovať na odkaz
                </CButton>

                <CButton
                    v-else-if="type === 'img'"
                    class="!min-h-0 !p-0 !text-xs text-current [&_.btn\_\_icon]:min-h-0 [&_.btn\_\_icon]:min-w-0"
                    :class="'!pl-0 !pr-0'"
                    icon-right
                    target="_blank"
                    variant="link"
                    @click="openImage"
                >
                    <template #icon>
                        <CIcon class="size-3" name="open-in-new-tab" />
                    </template>
                    Zobraziť obrázok
                </CButton>

                <TextClap v-else-if="type === 'text'" :key="visibleValue as string" as="span" :limit-characters="70" toggle-clap>
                    {{ visibleValue }}
                </TextClap>

                <div v-else>
                    {{ visibleValue }}
                </div>
            </div>
        </div>

        <CButton v-if="!isChecked" class="!min-h-0 !p-0 !text-xs" :disabled="disabled" variant="link" @click="useValue">
            Použiť
        </CButton>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import cloneDeep from 'lodash-es/cloneDeep'

import { LINKEDIN_DOMAIN_FIELDS } from '#shared/constants/linkedIn'
import type { UserPrivateProfile, UserProfile } from '#shared/types/User'
import { removeHTMLTags } from '#shared/utils'
import { isFieldSynchronized, isSynchronizationAvailable, previewFieldValue } from '#shared/utils/linkedIn'

type TFields = typeof LINKEDIN_DOMAIN_FIELDS
type TValue = UserProfile[keyof UserProfile] | UserPrivateProfile[keyof UserPrivateProfile]

const props = defineProps({
    value: {
        type: [String, Number, Boolean, Object, Array, Symbol] as PropType<TValue>,
        default: undefined
    },
    field: {
        type: String as PropType<TFields[keyof TFields][number]>,
        required: true
    },
    connectionData: {
        type: Object as PropType<any | null>,
        default: null
    },
    profileData: {
        type: Object as PropType<any | null>,
        default: null
    },
    disabled: {
        type: Boolean,
        default: false
    }
})

const emit = defineEmits(['input'])

const oldValue = ref(cloneDeep(props.value))

const normalizedProfileData = computed(() => {
    if (!props.profileData) return null
    if (props.profileData.profile && typeof props.profileData.profile === 'object') {
        return props.profileData.profile
    }
    return props.profileData
})

const type = computed((): 'text' | 'url' | 'img' | 'unknown' => {
    const value = previewFieldValue(
        isChecked.value,
        props.field,
        oldValue.value,
        normalizedProfileData.value
    )

    const isValueString = typeof value === 'string'

    if (isValueString) {
        const isValueUrl = value.startsWith('http')

        if (isValueUrl) return 'url'

        const isValueBase64 = value.startsWith('data:image')

        if (isValueBase64) return 'img'

        return 'text'
    }

    return 'unknown'
})

const isVisible = computed((): boolean => {
    return isSynchronizationAvailable(props.field, props.connectionData?.domains)
})

const isValueHidden = computed((): boolean => {
    return visibleValue.value === props.value && isChecked.value
})

const isChecked = computed((): boolean => {
    return isFieldSynchronized(props.field, props.connectionData?.syncFields)
})

const visibleValue = computed((): TValue => {
    const value = previewFieldValue(
        isChecked.value,
        props.field,
        oldValue.value,
        normalizedProfileData.value
    )
    if (typeof value === 'string' && type.value === 'text') return removeHTMLTags(value)
    return value
})

const visibleProvider = computed((): string => {
    return isChecked.value ? 'GROWNi' : 'LinkedIn'
})

watch(() => props.value, (newValue: TValue) => {
    if (!isVisible.value || isChecked.value) return
    oldValue.value = cloneDeep(newValue)
})

function useValue() {
    emit('input', visibleValue.value)
}

function openImage() {
    const value = visibleValue.value
    if (typeof value !== 'string') return

    const img = new Image()
    img.src = value

    if (process.client) {
        const tab = window.open(undefined, '_blank')
        if (!tab) return

        tab.document.write(img.outerHTML)
    }
}
</script>
